import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Volleyball,
    Search,
    Trophy,
    Calendar,
    Handshake,
    ExternalLink,
    List,
    Table as TableIcon,
    Medal,
    Crown,
    X,
    Users,
    BookOpen,
} from "lucide-react";

export default function PublicView() {
    const { slug } = useParams();
    const [params, setParams] = useSearchParams();
    const branch = params.get("rama") || "all";
    const categoriaFilter = params.get("cat") || "all";
    const tab = params.get("tab") || "standings";
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            try {
                const opts = branch !== "all" ? { params: { branch } } : {};
                const { data } = await api.get(`/public/${slug}`, opts);
                if (!cancelled) setData(data);
            } catch {
                if (!cancelled) setData(false);
            }
        };
        fetchData();
        // Refresh cada 8s para que el score en vivo se actualice casi al instante
        const t = setInterval(fetchData, 8000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [slug, branch]);

    const setParam = (k, v) => {
        const np = new URLSearchParams(params);
        if (!v) np.delete(k);
        else np.set(k, v);
        setParams(np);
    };

    const filteredMatches = useMemo(() => {
        if (!data) return [];
        const s = normalize(q);
        if (!s) return data.matches;
        // Divide por " vs ", " - " y espacios. Cada token debe aparecer.
        const tokens = s
            .split(/\s+vs\s+|\s+-\s+|\s+/)
            .map((t) => t.trim())
            .filter(Boolean);
        return data.matches.filter((m) => {
            const hay = normalize(
                [
                    m.local,
                    m.visitante,
                    m.inst_local,
                    m.inst_vis,
                    m.fase,
                    m.grupo,
                    m.hora,
                    `cancha ${m.cancha}`,
                    `c${m.cancha}`,
                    m.branch,
                    `${m.local || ""} vs ${m.visitante || ""}`,
                    `${m.visitante || ""} vs ${m.local || ""}`,
                ]
                    .filter(Boolean)
                    .join(" "),
            );
            return tokens.every((tok) => hay.includes(tok));
        });
    }, [data, q]);

    if (data === false)
        return (
            <div className="min-h-screen grid place-items-center text-slate-500">
                Torneo no encontrado.
            </div>
        );
    if (!data)
        return (
            <div className="min-h-screen grid place-items-center text-slate-500">
                Cargando…
            </div>
        );

    const { tournament, standings, sponsors = [] } = data;
    const allCats = tournament.categorias.filter((c) =>
        branch === "all" ? true : c.rama === branch,
    );
    const filteredCats =
        categoriaFilter === "all"
            ? allCats
            : allCats.filter((c) => c.id === categoriaFilter);

    const groupedRol = groupMatchesByDate(filteredMatches, tournament, filteredCats);

    // Podium: leemos del bracket (matches con grupo=Playoffs) la Final y Tercer Lugar
    const podium = (() => {
        const out = []; // [{ cat, oro, plata, bronce }]
        for (const cat of filteredCats) {
            const playoff = data.matches.filter(
                (m) =>
                    m.category_id === cat.id &&
                    m.grupo === "Playoffs" &&
                    m.status === "played",
            );
            const finalM = playoff.find((m) => m.fase === "Final");
            const tercerM = playoff.find((m) => m.fase === "Tercer Lugar");
            if (!finalM && !tercerM) continue;
            let oro = null,
                plata = null,
                bronce = null;
            if (finalM) {
                const a = finalM.set_ganados_local;
                const b = finalM.set_ganados_vis;
                if (a > b) {
                    oro = { name: finalM.local, inst: finalM.inst_local };
                    plata = { name: finalM.visitante, inst: finalM.inst_vis };
                } else if (b > a) {
                    oro = { name: finalM.visitante, inst: finalM.inst_vis };
                    plata = { name: finalM.local, inst: finalM.inst_local };
                }
            }
            if (tercerM) {
                const a = tercerM.set_ganados_local;
                const b = tercerM.set_ganados_vis;
                if (a > b)
                    bronce = { name: tercerM.local, inst: tercerM.inst_local };
                else if (b > a)
                    bronce = {
                        name: tercerM.visitante,
                        inst: tercerM.inst_vis,
                    };
            }
            out.push({ cat, oro, plata, bronce });
        }
        return out;
    })();

    const hasPodium = podium.length > 0;
    const hasSponsors = sponsors.length > 0;
    const hasReglamento = !!(tournament.reglamento && tournament.reglamento.trim());

    const fullUrl = (u) => {
        if (!u) return "";
        if (u.startsWith("http")) return u;
        return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <header
                className="relative bg-gradient-to-br from-ipn-900 to-ipn-950 text-white overflow-hidden"
                style={
                    tournament.banner_url
                        ? {
                              backgroundImage: `linear-gradient(180deg, rgba(70,0,32,0.75) 0%, rgba(70,0,32,0.9) 100%), url(${fullUrl(tournament.banner_url)})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                          }
                        : undefined
                }
                data-testid="public-banner"
            >
                <div className="max-w-7xl mx-auto px-6 py-12 relative">
                    <div className="flex items-center gap-2 text-white/70 text-xs font-bold tracking-widest uppercase">
                        <Volleyball className="w-4 h-4" />
                        {tournament.kind === "league" ? "Liga" : "Torneo Rápido"}
                    </div>
                    <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black drop-shadow-lg">
                        {tournament.name}
                    </h1>
                    <p className="mt-2 text-white/80">
                        {tournament.canchas} canchas ·{" "}
                        {tournament.categorias.length} categorías
                    </p>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Sponsors banner (top, siempre visible si hay) */}
                {hasSponsors && <SponsorsStrip sponsors={sponsors} />}

                {/* Search + category filter */}
                <div className="vp-card p-3 flex items-center gap-3 mb-6">
                    <Search className="w-5 h-5 text-slate-400 ml-2 shrink-0" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Busca equipo, institución, cancha, hora o grupo…"
                        className="border-none shadow-none focus-visible:ring-0 h-12"
                        data-testid="public-search"
                    />
                    {q.trim() && (
                        <div className="flex items-center gap-2 shrink-0">
                            <span
                                className="text-[10px] uppercase font-bold tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded-full"
                                data-testid="public-search-count"
                            >
                                {filteredMatches.length} resultado
                                {filteredMatches.length === 1 ? "" : "s"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setQ("")}
                                className="text-slate-400 hover:text-rose-600 transition-colors"
                                data-testid="public-search-clear"
                                aria-label="Limpiar búsqueda"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                    {allCats.length > 1 && (
                        <Select
                            value={categoriaFilter}
                            onValueChange={(v) =>
                                setParam("cat", v === "all" ? null : v)
                            }
                        >
                            <SelectTrigger
                                className="h-11 rounded-xl w-56"
                                data-testid="public-cat-filter"
                            >
                                <SelectValue placeholder="Categoría" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    Todas las categorías
                                </SelectItem>
                                {allCats.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.rama} · {c.nombre}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* Tabs: Tablas | Rol | Patrocinios */}
                <Tabs value={tab} onValueChange={(v) => setParam("tab", v)}>
                    <TabsList className="bg-white border border-slate-100 rounded-xl p-1 h-12">
                        <TabsTrigger
                            value="standings"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                            data-testid="public-tab-standings"
                        >
                            <Trophy className="w-4 h-4 mr-2" />
                            Estadísticas
                        </TabsTrigger>
                        <TabsTrigger
                            value="rol"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                            data-testid="public-tab-rol"
                        >
                            <Calendar className="w-4 h-4 mr-2" />
                            Rol de partidos
                        </TabsTrigger>
                        <TabsTrigger
                            value="teams"
                            className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                            data-testid="public-tab-teams"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Equipos
                        </TabsTrigger>
                        {hasPodium && (
                            <TabsTrigger
                                value="podium"
                                className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                                data-testid="public-tab-podium"
                            >
                                <Medal className="w-4 h-4 mr-2" />
                                Podium
                            </TabsTrigger>
                        )}
                        {hasSponsors && (
                            <TabsTrigger
                                value="sponsors"
                                className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                                data-testid="public-tab-sponsors"
                            >
                                <Handshake className="w-4 h-4 mr-2" />
                                Patrocinios
                            </TabsTrigger>
                        )}
                        {hasReglamento && (
                            <TabsTrigger
                                value="reglamento"
                                className="rounded-lg h-10 px-4 data-[state=active]:bg-ipn-800 data-[state=active]:text-white"
                                data-testid="public-tab-reglamento"
                            >
                                <BookOpen className="w-4 h-4 mr-2" />
                                Reglamento
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="standings" className="mt-6">
                        <StandingsGrid
                            cats={filteredCats}
                            standings={standings}
                            q={q}
                        />
                    </TabsContent>

                    <TabsContent value="rol" className="mt-6">
                        <RolList groups={groupedRol} />
                    </TabsContent>

                    <TabsContent value="teams" className="mt-6">
                        <TeamsView
                            cats={filteredCats}
                            matches={data.matches}
                            q={q}
                        />
                    </TabsContent>

                    {hasPodium && (
                        <TabsContent value="podium" className="mt-6">
                            <PodiumView podium={podium} />
                        </TabsContent>
                    )}

                    {hasSponsors && (
                        <TabsContent value="sponsors" className="mt-6">
                            <SponsorsFull sponsors={sponsors} />
                        </TabsContent>
                    )}

                    {hasReglamento && (
                        <TabsContent value="reglamento" className="mt-6">
                            <ReglamentoView text={tournament.reglamento} />
                        </TabsContent>
                    )}
                </Tabs>
            </main>

            <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-500">
                VolleyPro · {tournament.name}
            </footer>
        </div>
    );
}

function SponsorsStrip({ sponsors }) {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const fullUrl = (u) => {
        if (!u) return null;
        if (u.startsWith("http")) return u;
        if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
        return u;
    };
    return (
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-ipnYellow/40 via-white to-ipn-100/40 border border-ipnYellow-600/20 px-4 py-3 flex items-center gap-6 overflow-x-auto">
            <span className="text-[10px] uppercase tracking-widest font-bold text-ipn-800 flex-shrink-0">
                Apoyan esta liga
            </span>
            {sponsors.map((s) => (
                <a
                    key={s.id}
                    href={s.website || "#"}
                    target={s.website ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-bold hover:opacity-80 flex-shrink-0"
                    data-testid={`sponsor-strip-${s.id}`}
                >
                    {s.logo_url ? (
                        <img
                            src={fullUrl(s.logo_url)}
                            alt={s.nombre}
                            className="h-8 w-auto object-contain"
                        />
                    ) : (
                        <span className="font-display font-black text-ipn-800">
                            {s.nombre}
                        </span>
                    )}
                </a>
            ))}
        </div>
    );
}

function SponsorsFull({ sponsors }) {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const fullUrl = (u) => {
        if (!u) return null;
        if (u.startsWith("http")) return u;
        if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
        return u;
    };
    const tiers = {
        platino: { label: "Platino", color: "from-slate-200 to-slate-100", size: "h-20" },
        oro: { label: "Oro", color: "from-ipnYellow/40 to-ipnYellow/10", size: "h-16" },
        plata: { label: "Plata", color: "from-slate-100 to-white", size: "h-14" },
        bronce: { label: "Bronce", color: "from-orange-100 to-white", size: "h-12" },
    };
    const byTier = { platino: [], oro: [], plata: [], bronce: [] };
    sponsors.forEach((s) => byTier[s.tier]?.push(s));

    return (
        <div className="space-y-6">
            {["platino", "oro", "plata", "bronce"].map((t) => {
                const list = byTier[t];
                if (list.length === 0) return null;
                const cfg = tiers[t];
                return (
                    <section key={t}>
                        <h3 className="font-display text-lg font-black mb-3 text-ipn-800 uppercase tracking-widest text-sm">
                            {cfg.label}
                        </h3>
                        <div
                            className={`grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4`}
                        >
                            {list.map((s) => (
                                <a
                                    key={s.id}
                                    href={s.website || "#"}
                                    target={s.website ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className={`vp-card vp-card-hover p-6 bg-gradient-to-br ${cfg.color} flex items-center justify-center`}
                                    data-testid={`sponsor-card-${s.id}`}
                                >
                                    <div className="text-center">
                                        {s.logo_url ? (
                                            <img
                                                src={fullUrl(s.logo_url)}
                                                alt={s.nombre}
                                                className={`${cfg.size} w-auto mx-auto object-contain`}
                                            />
                                        ) : (
                                            <div className="font-display text-xl font-black text-ipn-900">
                                                {s.nombre}
                                            </div>
                                        )}
                                        <div className="mt-2 text-xs font-bold text-slate-500">
                                            {s.nombre}
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </section>
                );
            })}
            <div className="vp-card p-6 bg-gradient-to-br from-ipn-50 to-white text-center">
                <Handshake className="w-8 h-8 mx-auto text-ipn-800" />
                <h4 className="mt-2 font-display font-black">
                    ¿Quieres patrocinar?
                </h4>
                <p className="text-sm text-slate-500 mt-1">
                    Contacta al organizador para que tu marca aparezca aquí.
                </p>
            </div>
        </div>
    );
}

function PodiumView({ podium }) {
    return (
        <div className="space-y-8">
            {podium.map(({ cat, oro, plata, bronce }) => (
                <div
                    key={cat.id}
                    className="vp-card p-6 sm:p-10 relative overflow-hidden"
                    data-testid={`podium-card-${cat.id}`}
                >
                    {/* fondo deco */}
                    <div
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 20% 80%, #800040 0%, transparent 40%), radial-gradient(circle at 80% 20%, #FFD300 0%, transparent 40%)",
                        }}
                    />
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-6">
                            <span
                                className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${
                                    cat.rama === "femenil"
                                        ? "bg-ipn-100 text-ipn-900"
                                        : "bg-slate-800 text-white"
                                }`}
                            >
                                {cat.rama}
                            </span>
                            <h3 className="font-display font-black text-xl">
                                {cat.nombre}
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            {/* Plata - left */}
                            <PodiumStep
                                position={2}
                                medal={
                                    <Medal className="w-10 h-10 text-slate-400" />
                                }
                                team={plata}
                                bgClass="bg-slate-100 border-slate-200"
                                heightClass="h-40 md:h-48"
                                labelClass="text-slate-600"
                                testId={`podium-plata-${cat.id}`}
                            />
                            {/* Oro - center, taller */}
                            <PodiumStep
                                position={1}
                                medal={
                                    <Crown className="w-12 h-12 text-yellow-500" />
                                }
                                team={oro}
                                bgClass="bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300"
                                heightClass="h-52 md:h-64"
                                labelClass="text-amber-700"
                                testId={`podium-oro-${cat.id}`}
                                champion
                            />
                            {/* Bronce - right */}
                            <PodiumStep
                                position={3}
                                medal={
                                    <Medal className="w-10 h-10 text-orange-400" />
                                }
                                team={bronce}
                                bgClass="bg-orange-50 border-orange-200"
                                heightClass="h-32 md:h-40"
                                labelClass="text-orange-700"
                                testId={`podium-bronce-${cat.id}`}
                            />
                        </div>
                        <p className="text-center text-xs text-slate-400 mt-6 italic">
                            ¡Felicidades a los ganadores! Toma captura y
                            compártela en redes 🏐
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PodiumStep({
    position,
    medal,
    team,
    bgClass,
    heightClass,
    labelClass,
    testId,
    champion,
}) {
    return (
        <div className="flex flex-col items-center text-center" data-testid={testId}>
            <div className="mb-3">{medal}</div>
            {team ? (
                <>
                    <div
                        className={`font-display font-black uppercase break-words leading-tight ${
                            champion
                                ? "text-2xl md:text-3xl"
                                : "text-lg md:text-xl"
                        }`}
                    >
                        {team.name}
                    </div>
                    {team.inst && team.inst !== "-" && (
                        <div className="text-xs text-slate-500 mt-1 break-words">
                            {team.inst}
                        </div>
                    )}
                </>
            ) : (
                <div className="text-xs text-slate-400 italic">
                    Pendiente de partido
                </div>
            )}
            <div
                className={`mt-3 w-full ${heightClass} ${bgClass} rounded-t-2xl border-2 border-b-0 flex items-start justify-center pt-3`}
            >
                <span
                    className={`font-display font-black text-5xl md:text-6xl ${labelClass}`}
                >
                    {position}°
                </span>
            </div>
        </div>
    );
}

function TeamsView({ cats, matches, q }) {
    const s = normalize(q);
    const [selected, setSelected] = useState(null);
    if (cats.length === 0)
        return (
            <div className="vp-card p-8 text-center text-slate-500">
                Sin equipos para mostrar.
            </div>
        );
    return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cats.map((cat) => {
                const teams = (cat.teams || [])
                    .map((t) => {
                        // Compute grupo desde los partidos si existe
                        const teamMatch = matches.find(
                            (m) =>
                                m.category_id === cat.id &&
                                (m.local === t.nombre || m.visitante === t.nombre),
                        );
                        // Compute juegos/ganados
                        const played = matches.filter(
                            (m) =>
                                m.category_id === cat.id &&
                                m.status === "played" &&
                                !m.is_friendly &&
                                (m.local === t.nombre || m.visitante === t.nombre),
                        );
                        const pj = played.length;
                        const pg = played.filter((m) => {
                            const isLocal = m.local === t.nombre;
                            return isLocal
                                ? m.set_ganados_local > m.set_ganados_vis
                                : m.set_ganados_vis > m.set_ganados_local;
                        }).length;
                        return {
                            ...t,
                            grupo: teamMatch?.grupo || "-",
                            pj,
                            pg,
                        };
                    })
                    .filter((t) => {
                        if (!s) return true;
                        const hay = normalize(
                            [t.nombre, t.institucion, cat.nombre, t.grupo]
                                .filter(Boolean)
                                .join(" "),
                        );
                        return s.split(/\s+/).every((tok) => hay.includes(tok));
                    });

                if (teams.length === 0) return null;

                return (
                    <div
                        key={cat.id}
                        className="vp-card p-5"
                        data-testid={`teams-cat-${cat.id}`}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <span
                                className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${
                                    cat.rama === "femenil"
                                        ? "bg-ipn-100 text-ipn-900"
                                        : "bg-slate-800 text-white"
                                }`}
                            >
                                {cat.rama}
                            </span>
                            <h3 className="font-display font-bold text-base">
                                {cat.nombre}
                            </h3>
                            <span className="ml-auto text-xs font-bold text-slate-500">
                                {teams.length}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {teams.map((t, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() =>
                                        setSelected({ team: t, cat })
                                    }
                                    className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-ipn-300 hover:bg-ipn-50/40 transition-colors"
                                    data-testid={`team-card-${cat.id}-${i}`}
                                >
                                    <div className="flex items-start gap-2">
                                        {t.logo_url && (
                                            <img
                                                src={t.logo_url}
                                                alt=""
                                                className="w-10 h-10 rounded-full object-cover border border-slate-200 shrink-0"
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-sm break-words">
                                                {t.nombre}
                                            </div>
                                            {t.institucion && (
                                                <div className="text-xs text-slate-500 break-words">
                                                    {t.institucion}
                                                </div>
                                            )}
                                        </div>
                                        {t.grupo && t.grupo !== "-" && t.grupo !== "Único" && (
                                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-ipn-100 text-ipn-800 shrink-0">
                                                Grupo {t.grupo}
                                            </span>
                                        )}
                                    </div>
                                    {t.pj > 0 && (
                                        <div className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                            <span>PJ {t.pj}</span>
                                            <span className="text-emerald-600">
                                                PG {t.pg}
                                            </span>
                                            <span className="text-rose-600">
                                                PP {t.pj - t.pg}
                                            </span>
                                        </div>
                                    )}
                                    {t.players && t.players.length > 0 && (
                                        <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-ipn-700">
                                            <Users className="w-3 h-3 inline mr-1" />
                                            {t.players.length} jugadores · click para ver
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
        {selected && (
            <TeamDetailDialog
                team={selected.team}
                cat={selected.cat}
                matches={matches}
                open={!!selected}
                onOpenChange={(o) => !o && setSelected(null)}
            />
        )}
        </>
    );
}

function TeamDetailDialog({ team, cat, matches, open, onOpenChange }) {
    const played = matches
        .filter(
            (m) =>
                m.category_id === cat.id &&
                m.status === "played" &&
                (m.local === team.nombre || m.visitante === team.nombre),
        )
        .slice()
        .sort((a, b) => (b.hora || "").localeCompare(a.hora || ""))
        .slice(0, 10);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
                data-testid={`team-detail-${team.nombre}`}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 font-display text-xl">
                        {team.logo_url && (
                            <img
                                src={team.logo_url}
                                alt=""
                                className="w-12 h-12 rounded-full object-cover border border-slate-200"
                            />
                        )}
                        <div>
                            <div>{team.nombre}</div>
                            {team.institucion && (
                                <div className="text-xs font-normal text-slate-500">
                                    {team.institucion} · {cat.nombre} · {cat.rama}
                                </div>
                            )}
                        </div>
                    </DialogTitle>
                </DialogHeader>
                <section className="mt-4">
                    <h4 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-ipn-800" />
                        Roster oficial
                    </h4>
                    {team.players && team.players.length > 0 ? (
                        <div className="grid sm:grid-cols-2 gap-2">
                            {team.players.map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-3 p-2 rounded-lg border border-slate-100"
                                    data-testid={`public-player-${p.id}`}
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                                        {p.foto_url ? (
                                            <img
                                                src={p.foto_url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <Users className="w-5 h-5" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-bold text-sm truncate">
                                            {p.nombre}
                                        </div>
                                        {p.matricula && (
                                            <div className="text-[10px] font-mono text-slate-500">
                                                Mat. {p.matricula}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500">
                            El equipo aún no publicó su roster.
                        </div>
                    )}
                </section>
                <section className="mt-6">
                    <h4 className="font-display font-bold text-sm mb-2">
                        Últimos partidos jugados
                    </h4>
                    {played.length > 0 ? (
                        <div className="space-y-2">
                            {played.map((m) => {
                                const isLocal = m.local === team.nombre;
                                const won = isLocal
                                    ? m.set_ganados_local > m.set_ganados_vis
                                    : m.set_ganados_vis > m.set_ganados_local;
                                const rival = isLocal ? m.visitante : m.local;
                                return (
                                    <div
                                        key={m.id}
                                        className="flex items-center justify-between p-2 rounded-lg border border-slate-100"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-sm truncate">
                                                vs {rival}
                                            </div>
                                            <div className="text-[10px] text-slate-500">
                                                {m.hora} · C{m.cancha}
                                                {m.is_friendly ? " · AMISTOSO" : ""}
                                            </div>
                                        </div>
                                        <span
                                            className={`text-xs font-bold px-2 py-1 rounded ${
                                                won
                                                    ? "bg-emerald-100 text-emerald-800"
                                                    : "bg-rose-100 text-rose-800"
                                            }`}
                                        >
                                            {m.score || "-"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500">
                            Sin partidos jugados aún.
                        </div>
                    )}
                </section>
            </DialogContent>
        </Dialog>
    );
}

function StandingsGrid({ cats, standings, q }) {
    if (cats.length === 0)
        return (
            <div className="vp-card p-8 text-center text-slate-500">
                Sin categorías disponibles con este filtro.
            </div>
        );
    const matchesQ = (r) =>
        !q.trim()
            ? true
            : [r.equipo, r.institucion, r.grupo]
                  .filter(Boolean)
                  .some((x) => x.toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {cats.map((cat) => {
                const all = (standings[cat.id] || []).filter(matchesQ);
                const isGroups =
                    cat.formato === "grupos" &&
                    all.some((r) => r.grupo && r.grupo !== "Único");
                // Build groups map preserving order of appearance
                const groupsOrder = [];
                const groupsMap = {};
                if (isGroups) {
                    for (const r of all) {
                        const g = r.grupo || "Único";
                        if (!(g in groupsMap)) {
                            groupsMap[g] = [];
                            groupsOrder.push(g);
                        }
                        groupsMap[g].push(r);
                    }
                    groupsOrder.sort((a, b) => a.localeCompare(b));
                }
                return (
                    <div
                        key={cat.id}
                        className="vp-card p-6"
                        data-testid={`standings-card-${cat.id}`}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <span
                                className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${
                                    cat.rama === "femenil"
                                        ? "bg-ipn-100 text-ipn-900"
                                        : "bg-slate-800 text-white"
                                }`}
                            >
                                {cat.rama}
                            </span>
                            <h3 className="font-display font-bold text-lg">
                                {cat.nombre}
                            </h3>
                            {isGroups && (
                                <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                    {groupsOrder.length} grupos
                                </span>
                            )}
                        </div>
                        {all.length === 0 ? (
                            <div className="text-sm text-slate-500">
                                Aún sin resultados.
                            </div>
                        ) : isGroups ? (
                            <div className="space-y-5">
                                {groupsOrder.map((g) => (
                                    <div
                                        key={g}
                                        data-testid={`group-block-${cat.id}-${g}`}
                                    >
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-ipn-800 mb-1">
                                            {g}
                                        </div>
                                        <StandingsTable
                                            rows={groupsMap[g]}
                                            catId={cat.id}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <StandingsTable rows={all} catId={cat.id} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function StandingsTable({ rows, catId }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                    <tr>
                        <th className="text-left py-2">#</th>
                        <th className="text-left">Equipo</th>
                        <th>PJ</th>
                        <th>PG</th>
                        <th>PP</th>
                        <th>Dif</th>
                        <th>Ratio</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr
                            key={r.equipo}
                            className="border-t border-slate-100"
                            data-testid={`standings-row-${catId}-${i}`}
                        >
                            <td className="py-2 font-bold text-slate-400">
                                {i + 1}
                            </td>
                            <td className="font-semibold">
                                <div className="flex items-center gap-2">
                                    {r.logo_url && (
                                        <img
                                            src={r.logo_url}
                                            alt=""
                                            className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0"
                                        />
                                    )}
                                    <span>{r.equipo}</span>
                                </div>
                            </td>
                            <td className="text-center">{r.pj}</td>
                            <td className="text-center font-bold text-emerald-600">
                                {r.pg}
                            </td>
                            <td className="text-center text-rose-600">
                                {r.pp}
                            </td>
                            <td className="text-center">{r.dif}</td>
                            <td className="text-center">{r.ratio}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RolList({ groups }) {
    const [view, setView] = useState("list");
    const [fechaFilter, setFechaFilter] = useState("all");
    const [canchaFilter, setCanchaFilter] = useState("all");

    // Set of available fechas y canchas para los filtros
    const { fechas, canchas } = useMemo(() => {
        const fechasSet = new Set();
        const canchasSet = new Set();
        for (const g of groups) {
            for (const m of g.matches) {
                // Extract fecha del sublabel/label si es "YYYY-MM-DD"
                if (g.label && /\d{4}-\d{2}-\d{2}/.test(g.label)) {
                    const match = g.label.match(/\d{4}-\d{2}-\d{2}/);
                    if (match) fechasSet.add(match[0]);
                } else if (g.label && /Día \d+/i.test(g.label)) {
                    const m2 = g.label.match(/Día \d+/i);
                    if (m2) fechasSet.add(m2[0]);
                }
                canchasSet.add(m.cancha);
            }
        }
        return {
            fechas: Array.from(fechasSet).sort(),
            canchas: Array.from(canchasSet).sort((a, b) => a - b),
        };
    }, [groups]);

    const filteredGroups = useMemo(() => {
        return groups
            .map((g) => {
                const groupFecha = (g.label || "").match(/\d{4}-\d{2}-\d{2}|Día \d+/i);
                const fechaTag = groupFecha ? groupFecha[0] : null;
                if (fechaFilter !== "all" && fechaTag !== fechaFilter) return null;
                const filteredMatches =
                    canchaFilter === "all"
                        ? g.matches
                        : g.matches.filter(
                              (m) => String(m.cancha) === String(canchaFilter),
                          );
                if (filteredMatches.length === 0) return null;
                return { ...g, matches: filteredMatches };
            })
            .filter(Boolean);
    }, [groups, fechaFilter, canchaFilter]);

    if (groups.length === 0)
        return (
            <div className="vp-card p-8 text-center text-slate-500">
                Sin partidos para mostrar.
            </div>
        );
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {fechas.length > 1 && (
                        <select
                            value={fechaFilter}
                            onChange={(e) => setFechaFilter(e.target.value)}
                            className="h-9 rounded-xl border border-slate-200 px-3 text-xs font-bold bg-white uppercase tracking-widest"
                            data-testid="rol-fecha-filter"
                        >
                            <option value="all">Todas las fechas</option>
                            {fechas.map((f) => (
                                <option key={f} value={f}>
                                    {f}
                                </option>
                            ))}
                        </select>
                    )}
                    {canchas.length > 1 && (
                        <select
                            value={canchaFilter}
                            onChange={(e) => setCanchaFilter(e.target.value)}
                            className="h-9 rounded-xl border border-slate-200 px-3 text-xs font-bold bg-white uppercase tracking-widest"
                            data-testid="rol-cancha-filter"
                        >
                            <option value="all">Todas las canchas</option>
                            {canchas.map((c) => (
                                <option key={c} value={c}>
                                    Cancha {c}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                <ViewToggle view={view} onChange={setView} />
            </div>
            {filteredGroups.length === 0 && (
                <div className="vp-card p-8 text-center text-slate-500">
                    Ningún partido coincide con los filtros seleccionados.
                </div>
            )}
            {filteredGroups.map((grp) => (
                <div key={grp.key} className="vp-card overflow-hidden">
                    <div className="bg-ipn-50 border-b border-ipn-100 px-6 py-3 flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-ipn-900">
                                {grp.label}
                            </p>
                            {grp.sublabel && (
                                <p className="text-sm text-slate-600 mt-0.5">
                                    {grp.sublabel}
                                </p>
                            )}
                        </div>
                        <span className="text-sm font-bold text-slate-500">
                            {grp.matches.length} partidos
                        </span>
                    </div>
                    {view === "list" ? (
                        <div className="divide-y divide-slate-100">
                            {grp.matches.map((m) => (
                                <PublicMatchRow key={m.id} m={m} />
                            ))}
                        </div>
                    ) : (
                        <PublicMatchTable matches={grp.matches} />
                    )}
                </div>
            ))}
        </div>
    );
}

function ViewToggle({ view, onChange }) {
    return (
        <div
            className="inline-flex items-center gap-0.5 bg-white border border-slate-200 rounded-xl p-1"
            data-testid="rol-view-toggle"
        >
            <button
                onClick={() => onChange("list")}
                data-testid="rol-view-list"
                aria-label="Vista en lista"
                className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                    view === "list"
                        ? "bg-ipn-800 text-white"
                        : "text-slate-500 hover:text-ipn-900"
                }`}
            >
                <List className="w-4 h-4" />
                Lista
            </button>
            <button
                onClick={() => onChange("table")}
                data-testid="rol-view-table"
                aria-label="Vista en tabla"
                className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                    view === "table"
                        ? "bg-ipn-800 text-white"
                        : "text-slate-500 hover:text-ipn-900"
                }`}
            >
                <TableIcon className="w-4 h-4" />
                Tabla
            </button>
        </div>
    );
}

function PublicMatchTable({ matches }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="text-left px-3 py-2 font-bold">Hora</th>
                        <th className="text-left px-3 py-2 font-bold">Cancha</th>
                        <th className="text-left px-3 py-2 font-bold">Rama</th>
                        <th className="text-left px-3 py-2 font-bold">Local</th>
                        <th className="text-center px-3 py-2 font-bold">Resultado</th>
                        <th className="text-left px-3 py-2 font-bold">Visitante</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {matches.map((m) => {
                        const played = m.status === "played";
                        return (
                            <tr
                                key={m.id}
                                className="hover:bg-slate-50"
                                data-testid={`public-match-row-${m.id}`}
                            >
                                <td className="px-3 py-2 font-bold whitespace-nowrap">
                                    {m.hora || "—"}
                                </td>
                                <td className="px-3 py-2 text-ipn-800 font-bold whitespace-nowrap">
                                    C{m.cancha}
                                </td>
                                <td className="px-3 py-2">
                                    <span
                                        className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                            m.branch === "femenil"
                                                ? "bg-ipn-100 text-ipn-900"
                                                : "bg-slate-800 text-white"
                                        }`}
                                    >
                                        {m.branch}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-bold break-words">
                                    {m.local}
                                </td>
                                <td className="px-3 py-2 text-center whitespace-nowrap">
                                    <span
                                        className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-bold ${
                                            played
                                                ? "bg-emerald-100 text-emerald-800"
                                                : "bg-slate-100 text-slate-500"
                                        }`}
                                    >
                                        {played ? m.score : "vs"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-bold break-words">
                                    {m.visitante}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function PublicMatchRow({ m }) {
    const played = m.status === "played";
    const live = m.live;
    const friendly = m.is_friendly === true;

    // Sets breakdown desde m.sets (played) o m.live.sets (en curso).
    // Los played guardan [ [25,20], ... ]. Los live guardan [{score_local, score_vis}, ...].
    const rawSets = live?.sets ?? m.sets ?? [];
    const setsBreakdown = rawSets.map((s) =>
        Array.isArray(s)
            ? { score_local: s[0], score_vis: s[1] }
            : { score_local: s.score_local, score_vis: s.score_vis },
    );
    // Score global de sets ganados
    let setsL = 0,
        setsV = 0;
    if (played) {
        setsL = m.set_ganados_local ?? 0;
        setsV = m.set_ganados_vis ?? 0;
    } else if (live) {
        for (const s of setsBreakdown) {
            if (s.score_local > s.score_vis) setsL += 1;
            else if (s.score_vis > s.score_local) setsV += 1;
        }
    }

    // Estado label
    let stateLabel = "Programado";
    let stateClass = "bg-slate-100 text-slate-600";
    if (played) {
        stateLabel = "Final";
        stateClass = "bg-emerald-100 text-emerald-800";
    } else if (live) {
        stateLabel = `En vivo · Set ${live.current_set}`;
        stateClass = "bg-rose-600 text-white";
    }

    return (
        <div
            className="px-4 sm:px-6 py-4"
            data-testid={`public-match-${m.id}`}
        >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span
                    className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        m.branch === "femenil"
                            ? "bg-ipn-100 text-ipn-900"
                            : "bg-slate-800 text-white"
                    }`}
                >
                    {m.branch}
                </span>
                {friendly && (
                    <span
                        className="text-[10px] uppercase font-black px-1.5 py-0.5 rounded bg-amber-500 text-white tracking-widest"
                        data-testid={`friendly-badge-${m.id}`}
                    >
                        Amistoso
                    </span>
                )}
                <span
                    className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${stateClass} ${
                        live ? "animate-pulse" : ""
                    }`}
                    data-testid={
                        live
                            ? `live-badge-${m.id}`
                            : `state-badge-${m.id}`
                    }
                >
                    {live && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                    {stateLabel}
                </span>
                <span className="text-[10px] font-bold text-slate-500 ml-auto">
                    {m.hora || "—"} · Cancha {m.cancha}
                </span>
            </div>

            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-4">
                <TeamCell
                    name={m.local}
                    inst={m.inst_local}
                    align="left"
                    logo={m.logo_local}
                />
                <div className="flex items-center gap-2 sm:gap-3">
                    <ScoreBadge
                        n={setsL}
                        winner={played && setsL > setsV}
                        loser={played && setsL < setsV}
                        live={!!live}
                        testId={`score-local-${m.id}`}
                    />
                    <span className="text-slate-300 font-bold text-xs sm:text-sm">
                        vs
                    </span>
                    <ScoreBadge
                        n={setsV}
                        winner={played && setsV > setsL}
                        loser={played && setsV < setsL}
                        live={!!live}
                        testId={`score-vis-${m.id}`}
                    />
                </div>
                <TeamCell
                    name={m.visitante}
                    inst={m.inst_vis}
                    align="right"
                    logo={m.logo_vis}
                />
            </div>

            {setsBreakdown.length > 0 && (
                <div
                    className="mt-3 flex flex-wrap gap-1.5 justify-center"
                    data-testid={`sets-breakdown-${m.id}`}
                >
                    {setsBreakdown.map((s, i) => (
                        <span
                            key={i}
                            className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-700"
                        >
                            Set {i + 1}:{" "}
                            <span className="tabular-nums">
                                {s.score_local}-{s.score_vis}
                            </span>
                        </span>
                    ))}
                    {live && (
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-rose-600 text-white animate-pulse">
                            Set {live.current_set}:{" "}
                            <span className="tabular-nums">
                                {live.score_local}-{live.score_vis}
                            </span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function TeamCell({ name, inst, align, logo }) {
    const dir = align === "right" ? "flex-row-reverse" : "flex-row";
    return (
        <div
            className={`min-w-0 flex items-center gap-2 ${dir} ${
                align === "right" ? "text-right" : "text-left"
            }`}
        >
            {logo && (
                <img
                    src={logo}
                    alt=""
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-slate-200 shrink-0"
                />
            )}
            <div className="min-w-0 flex-1">
                <div className="font-black text-sm sm:text-base break-words leading-tight uppercase">
                    {name}
                </div>
                {inst && inst !== "-" && (
                    <div className="text-[10px] text-slate-500 break-words">
                        {inst}
                    </div>
                )}
            </div>
        </div>
    );
}

function ScoreBadge({ n, winner, loser, live, testId }) {
    return (
        <span
            data-testid={testId}
            className={`min-w-[36px] h-9 sm:min-w-[44px] sm:h-11 rounded-lg font-display font-black text-lg sm:text-2xl flex items-center justify-center tabular-nums transition-colors ${
                winner
                    ? "bg-emerald-100 text-emerald-800"
                    : loser
                      ? "bg-slate-100 text-slate-400"
                      : live
                        ? "bg-rose-600 text-white"
                        : "bg-slate-100 text-slate-500"
            }`}
        >
            {n}
        </span>
    );
}

function groupMatchesByDate(matches, tournament, cats) {
    const ids = new Set(cats.map((c) => c.id));
    const filtered = matches.filter((m) => ids.has(m.category_id));

    if (tournament.kind === "league") {
        const byJ = {};
        for (const m of filtered) {
            const key = m.jornada_id || "_draft";
            byJ[key] = byJ[key] || [];
            byJ[key].push(m);
        }
        const out = [];
        for (const j of tournament.jornadas || []) {
            if (byJ[j.id]) {
                out.push({
                    key: j.id,
                    label: j.fecha,
                    sublabel: `Modo: ${j.modo} · ${j.hora_inicio}–${j.hora_fin}`,
                    matches: sortByHoraCancha(byJ[j.id]),
                });
            }
        }
        return out;
    }

    const byCat = {};
    for (const m of filtered) {
        byCat[m.category_id] = byCat[m.category_id] || [];
        byCat[m.category_id].push(m);
    }
    const out = [];
    cats.filter((c) => byCat[c.id]).forEach((c) => {
        // Sub-group by dia para Torneo Rápido multi-día
        const byDia = {};
        for (const m of byCat[c.id]) {
            const d = m.dia || 1;
            byDia[d] = byDia[d] || [];
            byDia[d].push(m);
        }
        const dias = Object.keys(byDia)
            .map((x) => parseInt(x, 10))
            .sort((a, b) => a - b);
        if (dias.length <= 1) {
            out.push({
                key: c.id,
                label: `${c.rama} · ${c.nombre}`,
                sublabel: null,
                matches: sortByHoraCancha(byCat[c.id]),
            });
        } else {
            for (const d of dias) {
                out.push({
                    key: `${c.id}-d${d}`,
                    label: `${c.rama} · ${c.nombre} · Día ${d}`,
                    sublabel: `${byDia[d].length} partidos`,
                    matches: sortByHoraCancha(byDia[d]),
                });
            }
        }
    });
    return out;
}

function sortByHoraCancha(list) {
    return list.slice().sort((a, b) => {
        const da = a.dia || 1;
        const db = b.dia || 1;
        if (da !== db) return da - db;
        const ha = a.hora || "99:99";
        const hb = b.hora || "99:99";
        if (ha !== hb) return ha.localeCompare(hb);
        return (a.cancha || 0) - (b.cancha || 0);
    });
}


/**
 * Normaliza para búsqueda insensible a acentos + mayúsculas.
 * Ej: "Ex Búrritas" → "ex burritas".
 */
function normalize(str) {
    if (!str) return "";
    return String(str)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}


function ReglamentoView({ text }) {
    if (!text || !text.trim()) {
        return (
            <div className="vp-card p-8 text-center text-slate-500">
                El organizador aún no ha publicado el reglamento.
            </div>
        );
    }
    // Render simple: respeta saltos de línea y convierte ### en encabezados
    const blocks = text.split(/\n\n+/).map((block, i) => {
        if (/^###\s+/m.test(block.trim())) {
            const h = block.trim().replace(/^###\s+/, "");
            return (
                <h3
                    key={i}
                    className="font-display font-black text-lg sm:text-xl text-ipn-900 mt-6 first:mt-0"
                >
                    {h}
                </h3>
            );
        }
        return (
            <p
                key={i}
                className="text-slate-700 leading-relaxed whitespace-pre-wrap"
            >
                {block}
            </p>
        );
    });
    return (
        <article
            className="vp-card p-6 sm:p-8 space-y-3 max-w-4xl mx-auto"
            data-testid="public-reglamento"
        >
            {blocks}
        </article>
    );
}
